import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePersona } from "@/hooks/use-persona";
import { ArrowLeft, Send, MessageCircle, Plus, Mic, MicOff, Copy, PanelLeftClose, PanelLeftOpen, Check } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PersonaProvider } from "@/components/personas/PersonaProvider";
import { IntegratedVoiceChat } from "@/components/chat/IntegratedVoiceChat";
import { ChatNameDialog } from "@/components/chat/ChatNameDialog";

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  persona?: string;
  isVoice?: boolean;
}

interface ChatSession {
  id: string;
  name: string;
  messageCount: number;
  lastMessage: Date;
}

const ChatContent = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [businessAssessment, setBusinessAssessment] = useState<any>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([
    {
      id: '1',
      name: 'Strategic Planning Discussion',
      messageCount: 1,
      lastMessage: new Date()
    }
  ]);
  const [currentSessionId, setCurrentSessionId] = useState('1');
  const { currentPersona } = usePersona();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const checkAuthAndInitialize = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate("/auth");
          return;
        }

        // Fetch business assessment for personalized responses
        const { data: assessmentData } = await supabase
          .from('business_assessments')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        setBusinessAssessment(assessmentData);

        // Initialize with personalized welcome message
        const welcomeMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: assessmentData 
            ? `Hi, I'm CSO Agent, your AI Chief Strategy Officer. I've reviewed your business assessment for ${(assessmentData.data as any)?.companyName || 'your company'} and I'm ready to provide strategic guidance tailored to your ${(assessmentData.data as any)?.industry || 'business'} needs. How can I help you today?`
            : `Hi, I'm CSO Agent, your AI Chief Strategy Officer. How can I help you today?`,
          timestamp: new Date(),
          persona: currentPersona
        };

        setMessages([welcomeMessage]);
        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing chat:', error);
        toast({
          title: "Error",
          description: "Failed to initialize chat. Please try again.",
          variant: "destructive",
        });
      }
    };

    checkAuthAndInitialize();
  }, [navigate, toast, currentPersona]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      console.log('Sending message to chat completion:', input);

      const { data, error } = await supabase.functions.invoke('chat-completion', {
        body: { 
          message: input,
          sessionId: sessionId,
          persona: currentPersona,
          businessContext: businessAssessment // Include business assessment context
        }
      });

      if (error) throw error;

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.content || 'I apologize, but I encountered an issue processing your request. Please try again.',
        timestamp: new Date(),
        persona: currentPersona
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error: any) {
      console.error('Chat error:', error);
      toast({
        title: "Chat Error",
        description: "Failed to get response. Please try again.",
        variant: "destructive",
      });

      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'I apologize, but I\'m experiencing technical difficulties. Please try again in a moment.',
        timestamp: new Date(),
        persona: currentPersona
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleVoiceChat = () => {
    setIsVoiceActive(!isVoiceActive);
  };

  const handleVoiceMessage = async (text: string) => {
    console.log('🗣️ Handling voice message:', text);
    
    // Add user voice message to chat
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: new Date(),
      persona: currentPersona,
      isVoice: true
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      console.log('🔄 Calling chat-completion function...');
      
      // Process the voice message through chat completion
      const { data, error } = await supabase.functions.invoke('chat-completion', {
        body: { 
          message: text,
          sessionId: sessionId,
          persona: currentPersona,
          businessContext: businessAssessment
        }
      });

      console.log('📞 Chat-completion response:', { data, error });

      if (error) {
        console.error('❌ Chat-completion error:', error);
        throw error;
      }

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.content || 'I apologize, but I encountered an issue processing your request.',
        timestamp: new Date(),
        persona: currentPersona,
        isVoice: true
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Auto-play the assistant's response when voice chat is active
      if (isVoiceActive && data.content) {
        try {
          console.log('🔊 Converting response to speech...');
          
          const { data: audioData, error: audioError } = await supabase.functions.invoke('text-to-speech', {
            body: { text: data.content }
          });

          console.log('📞 Text-to-speech response:', { audioData, audioError });

          if (!audioError && audioData?.audioBase64) {
            console.log('▶️ Playing audio response...');
            
            const audioBlob = new Blob([
              Uint8Array.from(atob(audioData.audioBase64), c => c.charCodeAt(0))
            ], { type: 'audio/mpeg' });
            
            const audioUrl = URL.createObjectURL(audioBlob);
            const audio = new Audio(audioUrl);
            
            audio.onended = () => {
              console.log('🔇 Audio playback ended');
              URL.revokeObjectURL(audioUrl);
            };
            
            audio.onerror = (audioPlayError) => {
              console.error('❌ Audio playback error:', audioPlayError);
              URL.revokeObjectURL(audioUrl);
            };
            
            await audio.play();
          } else {
            console.warn('⚠️ No audio data received from text-to-speech');
          }
        } catch (audioError) {
          console.error('❌ Audio processing error:', audioError);
          // Don't show toast for audio errors as the text response is still valid
        }
      }

    } catch (error: any) {
      console.error('❌ Voice message error:', error);
      toast({
        title: "Chat Error",
        description: "Failed to process voice message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


  const createNewSession = () => {
    const newSession: ChatSession = {
      id: crypto.randomUUID(),
      name: 'New Discussion',
      messageCount: 0,
      lastMessage: new Date()
    };
    setSessions(prev => [newSession, ...prev]);
    setCurrentSessionId(newSession.id);
    setMessages([]);
  };

  const copyToClipboard = async (text: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      toast({
        title: "Copied!",
        description: "Message copied to clipboard",
      });
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Failed to copy message to clipboard",
        variant: "destructive",
      });
    }
  };

  const updateSessionName = (sessionId: string, newName: string) => {
    setSessions(prev => prev.map(session => 
      session.id === sessionId ? { ...session, name: newName } : session
    ));
  };

  const currentSession = sessions.find(s => s.id === currentSessionId);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600">Initializing chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Hide support bubble throughout chat */}
      <style>{'[data-support-bubble] { display: none !important; }'}</style>
      
      {/* Integrated Voice Chat */}
      <IntegratedVoiceChat
        isActive={isVoiceActive}
        onToggle={toggleVoiceChat}
        onVoiceMessage={handleVoiceMessage}
        onPlayResponse={(text) => {
          // Auto-play response audio when voice chat is active
          const lastMessage = messages[messages.length - 1];
          if (lastMessage?.role === 'assistant' && lastMessage.content === text) {
            // Audio will play automatically via the component
          }
        }}
      />

      {/* Sidebar */}
      {sidebarVisible && (
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Dashboard
              </Button>
              <Button
                size="sm"
                onClick={createNewSession}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <h2 className="font-semibold text-gray-900">Chat Sessions</h2>
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-2">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => setCurrentSessionId(session.id)}
                  className={`p-3 rounded-lg cursor-pointer mb-2 transition-colors group ${
                    currentSessionId === session.id
                      ? 'bg-blue-50 border-blue-200'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm text-gray-900 truncate flex-1">
                      {session.name}
                    </h3>
                    <ChatNameDialog
                      currentName={session.name}
                      onNameChange={(name) => updateSessionName(session.id, name)}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {session.messageCount} messages • {session.lastMessage.toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Persona Card */}
          <div className="p-4 border-t border-gray-200">
            <Card className="p-4 bg-gradient-to-br from-purple-500 to-blue-600 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-sm">👔</span>
                </div>
                <div>
                  <h3 className="font-semibold">Chief Strategy Officer</h3>
                  <p className="text-xs opacity-90">Strategic Leadership</p>
                </div>
              </div>
              <p className="text-xs opacity-90 mb-3">
                Focus on long-term strategy, market analysis, and competitive positioning
              </p>
              <div className="space-y-1">
                <div className="text-xs px-2 py-1 bg-white/20 rounded">Strategic Planning</div>
                <div className="text-xs px-2 py-1 bg-white/20 rounded">Market Analysis</div>
                <div className="text-xs px-2 py-1 bg-white/20 rounded">Competitive Intelligence</div>
                <div className="text-xs px-2 py-1 bg-white/20 rounded">Business Development</div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarVisible(!sidebarVisible)}
                className="flex items-center gap-2"
              >
                {sidebarVisible ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
              </Button>
              <MessageCircle className="h-5 w-5 text-blue-600" />
              <h1 className="text-lg font-semibold text-gray-900">{currentSession?.name || 'Chat'}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleVoiceChat}
                className={`flex items-center gap-2 ${
                  isVoiceActive ? 'bg-red-50 border-red-200 text-red-700' : ''
                }`}
              >
                {isVoiceActive ? (
                  <>
                    <MicOff className="h-4 w-4" />
                    End Voice Chat
                  </>
                ) : (
                  <>
                    <Mic className="h-4 w-4" />
                    Voice Chat
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Voice Status */}
        {isVoiceActive && (
          <div className="bg-green-50 border-b border-green-200 px-4 py-2">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              Voice chat active - press "Record" to start speaking, then "Stop Recording" when finished
            </div>
          </div>
        )}


        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4 max-w-4xl mx-auto">
            {messages.map((message) => (
              <div key={message.id} className="flex items-start gap-3">
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm">🤖</span>
                  </div>
                )}
                <div className={`flex-1 ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div
                    className={`inline-block max-w-[80%] p-3 rounded-lg group relative ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{message.content}</div>
                    <div className={`text-xs mt-2 flex items-center gap-2 ${
                      message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      <span>{message.timestamp.toLocaleTimeString()}</span>
                      {message.isVoice && (
                        <Badge variant="secondary" className="text-xs">
                          Voice
                        </Badge>
                      )}
                      {message.role === 'assistant' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(message.content, message.id)}
                          className={`h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                            copiedMessageId === message.id ? 'opacity-100' : ''
                          }`}
                        >
                          {copiedMessageId === message.id ? (
                            <Check className="h-3 w-3 text-green-600" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-sm">🤖</span>
                </div>
                <div className="bg-white border border-gray-200 text-gray-900 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span>Thinking</span>
                    <div className="flex gap-1">
                      <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="bg-white border-t border-gray-200 p-4">
          {isVoiceActive && (
            <div className="mb-4">
              <IntegratedVoiceChat
                isActive={isVoiceActive}
                onToggle={toggleVoiceChat}
                onVoiceMessage={handleVoiceMessage}
                onPlayResponse={(text) => {
                  // Auto-play response when voice is active
                }}
              />
            </div>
          )}
          <div className="flex gap-2 max-w-4xl mx-auto">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask your CSO Agent..."
              disabled={isLoading}
              className="flex-1"
            />
            <Button
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
              size="icon"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Chat = () => {
  return (
    <PersonaProvider>
      <ChatContent />
    </PersonaProvider>
  );
};

export default Chat;
